import React, { useState, useEffect, useCallback } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Box, IconButton, Tooltip, Chip, Select,
  FormControl, InputLabel, Grid, Pagination, Stack, Typography
} from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import {
  getAllShortsForAdmin,
  updateShortStatus,
  updateShortByAdmin,
  deleteShortByAdmin,
  getAllReporters
} from "../../../services/adminService";
import {
  getCategories,
  getSubcategories,
  getCountries,
  getStatesByCountry,
  getCitiesByState,
} from "../../../services/apiService";

const getStatusColor = (status) => {
  switch (status) {
    case 'published': return 'success';
    case 'pending_approval': return 'warning';
    case 'rejected': return 'error';
    case 'draft': return 'default';
    default: return 'primary';
  }
};

const AllShortsAdminManagement = () => {
  const [shorts, setShorts] = useState([]);
  const [statusUpdating, setStatusUpdating] = useState(null);

  // Filters
  const [reportersDropdown, setReportersDropdown] = useState([]);
  const [categoriesDropdown, setCategoriesDropdown] = useState([]);
  const [countriesDropdown, setCountriesDropdown] = useState([]);
  const [statesDropdown, setStatesDropdown] = useState([]);
  const [filterCities, setFilterCities] = useState([]);

  const [filters, setFilters] = useState({
    status: "all",
    category: "",
    reporter: "",
    state: "",
    city: "",
    startDate: "",
    endDate: "",
    search: "",
  });

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editingShort, setEditingShort] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [subCategoriesDropdown, setSubCategoriesDropdown] = useState([]);
  const [editStatesDropdown, setEditStatesDropdown] = useState([]);
  const [editCitiesDropdown, setEditCitiesDropdown] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Player
  const [openPlayer, setOpenPlayer] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");

  const fetchShorts = useCallback(async (pageNumber = page) => {
    try {
      const data = await getAllShortsForAdmin({ page: pageNumber, limit, ...filters });
      setShorts(data.data || []);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      console.error("Error fetching shorts:", error);
    }
  }, [limit, page, filters]);

  useEffect(() => { fetchShorts(page); }, [fetchShorts, page]);

  useEffect(() => {
    const loadInitial = async () => {
      try {
        const [catsRes, countriesRes, reportersRes] = await Promise.all([
          getCategories(), getCountries(), getAllReporters()
        ]);
        setCategoriesDropdown(catsRes.data || []);
        setCountriesDropdown(countriesRes.data || []);
        setReportersDropdown(reportersRes.data || []);
        if (countriesRes.data?.length > 0) {
          const res = await getStatesByCountry(countriesRes.data[0]._id);
          setStatesDropdown(res.data || []);
        }
      } catch (err) {
        console.error("Initial load failed:", err);
      }
    };
    loadInitial();
  }, []);

  useEffect(() => {
    if (filters.state) {
      getCitiesByState(filters.state).then(res => setFilterCities(res.data || []));
    } else {
      setFilterCities([]);
    }
  }, [filters.state]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      status: "all", category: "", reporter: "", state: "", city: "",
      startDate: "", endDate: "", search: "",
    });
    setPage(1);
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      setStatusUpdating(id);
      await updateShortStatus(id, newStatus);
      fetchShorts(page);
    } catch (error) {
      console.error("Status update error:", error);
      alert("Failed to update status.");
    } finally {
      setStatusUpdating(null);
    }
  };

  // ---- Edit dialog logic ----
  const handleEditOpen = async (item) => {
    setEditingShort(item);
    setVideoFile(null);
    setThumbnailFile(null);

    // Load subcategories/states/cities based on current item's IDs
    if (item.category?._id) {
      const res = await getSubcategories(item.category._id);
      setSubCategoriesDropdown(res.data || []);
    } else {
      setSubCategoriesDropdown([]);
    }
    if (item.country?._id) {
      const res = await getStatesByCountry(item.country._id);
      setEditStatesDropdown(res.data || []);
    } else {
      setEditStatesDropdown([]);
    }
    if (item.state?._id) {
      const res = await getCitiesByState(item.state._id);
      setEditCitiesDropdown(res.data || []);
    } else {
      setEditCitiesDropdown([]);
    }

    setEditFormData({
      title: item.title || "",
      description: item.description || "",
      category: item.category?._id || "",
      subCategory: item.subCategory?._id || "",
      country: item.country?._id || "",
      state: item.state?._id || "",
      city: item.city?._id || "",
      status: item.status || "",
    });
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditingShort(null);
    setEditFormData({});
    setSubCategoriesDropdown([]);
    setEditStatesDropdown([]);
    setEditCitiesDropdown([]);
    setVideoFile(null);
    setThumbnailFile(null);
  };

  const handleEditFormChange = async (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));

    if (name === "category") {
      setEditFormData(prev => ({ ...prev, subCategory: "" }));
      const res = value ? await getSubcategories(value) : { data: [] };
      setSubCategoriesDropdown(res.data || []);
    } else if (name === "country") {
      setEditFormData(prev => ({ ...prev, state: "", city: "" }));
      const res = value ? await getStatesByCountry(value) : { data: [] };
      setEditStatesDropdown(res.data || []);
      setEditCitiesDropdown([]);
    } else if (name === "state") {
      setEditFormData(prev => ({ ...prev, city: "" }));
      const res = value ? await getCitiesByState(value) : { data: [] };
      setEditCitiesDropdown(res.data || []);
    }
  };

  // ✅ Backend names expect (not IDs) — convert selected IDs to names before submit
  const handleUpdateSubmit = async () => {
    if (!editingShort) return;
    setUpdating(true);
    try {
      const categoryName = categoriesDropdown.find(c => c._id === editFormData.category)?.name || "";
      const subCategoryName = subCategoriesDropdown.find(s => s._id === editFormData.subCategory)?.name || "";
      const countryName = countriesDropdown.find(c => c._id === editFormData.country)?.name || "";
      const stateName = editStatesDropdown.find(s => s._id === editFormData.state)?.name || "";
      const cityName = editCitiesDropdown.find(c => c._id === editFormData.city)?.name || "";

      const data = new FormData();
      data.append("title", editFormData.title);
      data.append("description", editFormData.description);
      if (categoryName) data.append("category", categoryName);
      if (subCategoryName) data.append("subCategory", subCategoryName);
      if (countryName) data.append("country", countryName);
      if (stateName) data.append("state", stateName);
      if (cityName) data.append("city", cityName);
      if (editFormData.status) data.append("status", editFormData.status);

      if (videoFile) data.append("video", videoFile);
      if (thumbnailFile) data.append("thumbnail", thumbnailFile);

      await updateShortByAdmin(editingShort._id, data);
      handleEditClose();
      fetchShorts(page);
    } catch (error) {
      console.error("Error updating short:", error);
      alert(error.response?.data?.message || "Update failed");
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this short?")) return;
    try {
      await deleteShortByAdmin(id);
      fetchShorts(page);
    } catch (error) {
      console.error("Error deleting short:", error);
    }
  };

  const handlePlayVideo = (url) => {
    setCurrentVideoUrl(url);
    setOpenPlayer(true);
  };

  return (
    <div className="p-4">
      <Typography variant="h5" gutterBottom>Shorts Management</Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f7fa', borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Reporter</InputLabel>
              <Select name="reporter" value={filters.reporter} label="Reporter" onChange={handleFilterChange}>
                <MenuItem value="">All Reporters</MenuItem>
                {reportersDropdown.map((rep) => (
                  <MenuItem key={rep.id} value={rep.id}>
                    {rep.name}{rep.city?.name ? ` (${rep.city.name})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select name="status" value={filters.status} label="Status" onChange={handleFilterChange}>
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending_approval">Pending</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select name="category" value={filters.category} label="Category" onChange={handleFilterChange}>
                <MenuItem value="">All Categories</MenuItem>
                {categoriesDropdown.map((cat) => (
                  <MenuItem key={cat._id} value={cat._id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>State</InputLabel>
              <Select name="state" value={filters.state} label="State" onChange={handleFilterChange}>
                <MenuItem value="">All States</MenuItem>
                {statesDropdown.map((st) => (
                  <MenuItem key={st._id} value={st._id}>{st.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small" disabled={!filters.state}>
              <InputLabel>City</InputLabel>
              <Select name="city" value={filters.city} label="City" onChange={handleFilterChange}>
                <MenuItem value="">All Cities</MenuItem>
                {filterCities.map((ct) => (
                  <MenuItem key={ct._id} value={ct._id}>{ct.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid> */}

          <Grid item xs={12} sm={6} md={2.4}>
            <TextField fullWidth size="small" label="Search by title" name="search" value={filters.search} onChange={handleFilterChange} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" type="date" label="From Date" name="startDate" InputLabelProps={{ shrink: true }} value={filters.startDate} onChange={handleFilterChange} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" type="date" label="To Date" name="endDate" InputLabelProps={{ shrink: true }} value={filters.endDate} onChange={handleFilterChange} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button fullWidth variant="outlined" color="secondary" onClick={clearFilters} startIcon={<ClearIcon />} sx={{ height: "40px" }}>
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Video</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Reporter</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Approval</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shorts.map((item, idx) => (
              <TableRow key={item._id}>
                <TableCell>{(page - 1) * limit + idx + 1}</TableCell>
                <TableCell>
                  {item.thumbnailUrl ? (
                    <img
                      src={item.thumbnailUrl}
                      alt={item.title}
                      style={{ width: "80px", height: "100px", objectFit: "cover", borderRadius: ".4rem", cursor: "pointer" }}
                      onClick={() => handlePlayVideo(item.videoUrl)}
                    />
                  ) : (
                    <video
                      src={item.videoUrl}
                     width="110"
                        height="150" 
                        muted
                        loop
                        autoPlay
                        style={{
                          borderRadius: 8,
                          objectFit: "cover",
                        }}
                      style={{ objectFit: "cover", borderRadius: ".4rem", cursor: "pointer" }}
                      onClick={() => handlePlayVideo(item.videoUrl)}
                    />
                  )}
                </TableCell>
                <TableCell>{item.title}</TableCell>
                <TableCell>{item.category?.name || "-"}</TableCell>
                <TableCell>{item.createdBy?.name || item.createdBy?.username}</TableCell>
                <TableCell>
                  <Chip label={item.status?.toUpperCase()} color={getStatusColor(item.status)} size="small" />
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={1} justifyContent="center">
                    {item.status !== 'published' && (
                      <Button size="small" variant="contained" color="success"
                        disabled={statusUpdating === item._id}
                        onClick={() => handleStatusUpdate(item._id, 'published')}>
                        {statusUpdating === item._id ? '...' : 'Approve'}
                      </Button>
                    )}
                    {item.status !== 'rejected' && (
                      <Button size="small" variant="contained" color="error"
                        disabled={statusUpdating === item._id}
                        onClick={() => handleStatusUpdate(item._id, 'rejected')}>
                        {statusUpdating === item._id ? '...' : 'Reject'}
                      </Button>
                    )}
                    {(item.status === 'rejected' || item.status === 'published') && (
                      <Button size="small" variant="outlined"
                        disabled={statusUpdating === item._id}
                        onClick={() => handleStatusUpdate(item._id, 'pending_approval')}>
                        Reset
                      </Button>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="center">
                  <Box display="flex" gap={1} justifyContent="center">
                    <Tooltip title="Edit">
                      <IconButton color="primary" size="small" onClick={() => handleEditOpen(item)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton color="error" size="small" onClick={() => handleDelete(item._id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack spacing={2} sx={{ mt: 2 }} alignItems="center">
        <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} color="primary" />
        <Select value={limit} onChange={(e) => { setLimit(e.target.value); setPage(1); }} size="small">
          {[5, 10, 20, 50].map((l) => <MenuItem key={l} value={l}>{l} per page</MenuItem>)}
        </Select>
      </Stack>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={handleEditClose} fullWidth maxWidth="sm">
        <DialogTitle>Edit Short</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Title" name="title" fullWidth margin="dense"
            value={editFormData.title || ""} onChange={handleEditFormChange}
          />
          <TextField
            label="Description" name="description" fullWidth multiline rows={2} margin="dense"
            value={editFormData.description || ""} onChange={handleEditFormChange}
          />

          
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select name="status" value={editFormData.status || ""} label="Status" onChange={handleEditFormChange}>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="pending_approval">Pending Approval</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>

<Box sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', bgcolor: '#000', width: 160, height: 220 }}>
            <video
              src={videoFile ? URL.createObjectURL(videoFile) : editingShort?.videoUrl}
              controls
              muted
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Box>
          <Box mt={2}>
            <Typography variant="body2" gutterBottom>Replace Video (optional)</Typography>
            <input type="file" accept="video/*" onChange={(e) => setVideoFile(e.target.files[0])} />
          </Box>
          
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} disabled={updating}>Cancel</Button>
          <Button onClick={handleUpdateSubmit} variant="contained" color="primary" disabled={updating}>
            {updating ? "Updating..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Player Modal */}
      <Dialog open={openPlayer} onClose={() => setOpenPlayer(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Preview
          <IconButton aria-label="close" onClick={() => setOpenPlayer(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <video src={currentVideoUrl} width="100%" controls autoPlay />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllShortsAdminManagement;